import {
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Popper,
  Stack,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { FunnelIcon } from "@phosphor-icons/react/dist/csr/Funnel";
import { ReactNode, useState } from "react";
import { JSX } from "react/jsx-runtime";
import { IconForFilter } from "../IconForFilter/IconForFilter";
import { SourceLeaf, SourceNode } from "../common/FilterPublishedArtifacts";

type SelectableItems = "reportDate" | "startedDate" | "name" | "purpose" | "status";

const ClearThisFilter = ({ onClick }: { onClick: () => void }) => {
  return (
    <Typography
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
        },
        color: theme.palette.secondary60.main,
      })}
      variant="body.small.link"
      onClick={onClick}
    >
      Remove this filter
    </Typography>
  );
};

const addChildToNode = (targetNodes: SourceNode[], child: SourceLeaf, node: SourceNode) => {
  const existingNode = targetNodes.find((n) => n.title === node.title);
  if (existingNode) {
    existingNode.children.push(child);
  } else {
    targetNodes.push({ ...node, children: [child] });
  }
};

type FilterRowProps = {
  value: string;
  onClick: (value: string) => void;
  text: string;
  isSelected?: boolean;
  icon?: JSX.Element;
};

const FilterRow = ({ value, onClick, text, isSelected, icon }: FilterRowProps) => {
  return (
    <Stack
      direction="row"
      sx={{
        gap: "12px",
        alignItems: "center",
        "&:hover": {
          cursor: "pointer",
        },
      }}
      onClick={() => onClick(value)}
    >
      <Checkbox checked={isSelected} />
      {icon}
      <Typography variant="body.medium.regular" sx={{ color: "text40.main" }}>
        {text}
      </Typography>
    </Stack>
  );
};

const ItemContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  "&:hover": {
    cursor: "pointer",
    backgroundColor: theme.palette.surfaceLight30.main,
  },
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: isSelected ? theme.palette.surfaceLight30.main : undefined,
  width: "165px",
  height: "44px",
  paddingLeft: "20px",
  paddingRight: "20px",
  borderTopRightRadius: "8px",
  borderBottomRightRadius: "8px",
  gap: "8px",
}));

const Item = ({
  text,
  isSelected,
  onClick,
  hasFiltersApplied,
}: {
  text: string;
  isSelected: boolean;
  onClick: () => void;
  hasFiltersApplied?: boolean;
}) => {
  return (
    <ItemContainer isSelected={isSelected} onClick={onClick}>
      <Typography variant="heading.seven" sx={{ color: "text40.main" }}>
        {text}
      </Typography>
      {hasFiltersApplied ? (
        <Stack
          sx={(theme) => ({
            minWidth: "8px",
            minHeight: "8px",
            borderRadius: "50%",
            backgroundColor: theme.palette.accentDanger30.main,
          })}
        />
      ) : null}
    </ItemContainer>
  );
};

type FilterPanelProps = {
  filterItems: ReactNode;
  rightPanel: ReactNode;
  filterCount?: number;
  onClearAllFilters: () => void;
  biggerWidth?: boolean;
  showClearAllFilters: boolean;
  loading?: boolean;
};

const FilterPanel = ({
  filterCount = 0,
  filterItems,
  rightPanel,
  onClearAllFilters,
  biggerWidth,
  loading,
  showClearAllFilters,
}: FilterPanelProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  if (loading) {
    return (
      <Stack>
        <CircularProgress size="15px" />
      </Stack>
    );
  }
  return (
    <>
      <IconForFilter
        iconComponent={
          <IconButton onClick={handleOpen} data-testid="funnel-button">
            <FunnelIcon size={24} color={theme.palette.text50.main} />
          </IconButton>
        }
        count={filterCount}
        bottom="23px"
        left="24px"
      />

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={(theme) => ({
          zIndex: theme.zIndex.modal,
        })}
      >
        <ClickAwayListener mouseEvent="onMouseUp" onClickAway={() => setAnchorEl(null)}>
          <Stack
            direction="row"
            sx={{
              height: "600px",
              minWidth: biggerWidth ? "1100px" : "550px",
              maxWidth: "1100px",
              gap: "17px",
              backgroundColor: theme.palette.surfaceLight10.main,
              border: `1px solid ${theme.palette.surfaceLight30.main}`,
              boxShadow: theme.mixins.shadowSurfaceLight10,
              ...theme.applyStyles("dark", {
                boxShadow: theme.mixins.shadowSurfaceDark10,
              }),
              transition: "min-width 0.3s",
            }}
            data-testid="filter-panel"
          >
            <Stack
              sx={{ padding: "20px 20px 20px 0px", borderRight: `1px solid ${theme.palette.surfaceLight50.main}` }}
            >
              {filterItems}
              {showClearAllFilters ? (
                <Stack direction="row" sx={{ alignItems: "center", width: "100%", height: "61px", padding: "20px" }}>
                  <Typography
                    sx={(theme) => ({
                      "&:hover": {
                        cursor: "pointer",
                      },
                      color: theme.palette.secondary60.main,
                    })}
                    variant="body.small.link"
                    onClick={onClearAllFilters}
                  >
                    Clear all filters
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
            <Stack sx={{ width: biggerWidth ? "100%" : "350px", padding: "20px 20px 20px 0px" }}>{rightPanel}</Stack>
          </Stack>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export { addChildToNode, ClearThisFilter, FilterPanel, FilterRow, Item };
export type { SelectableItems };
